import { useState } from 'react';
import clsx from 'clsx';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { FormTextarea } from '@/components/FormTextarea';
import { AttachmentList } from '../AttachmentList';
import { AttachmentPicker } from '../AttachmentPicker';
import type { PendingFile } from '../../services/dpAttachmentUpload';
import type { TicketThreadProps } from './ticketThread.types';

const MAX_REPLY_LENGTH = 4000;

function formatTime(value: { toDate?: () => Date } | null | undefined): string {
    const date = typeof value?.toDate === 'function' ? value.toDate() : null;
    if (!date) return '';
    return date.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export function TicketThread({ messages, viewerEmail, isLoading, canReply, replyPlaceholder, isSending, onSend }: TicketThreadProps) {
    const [body, setBody] = useState('');
    const [files, setFiles] = useState<PendingFile[]>([]);
    const [sending, setSending] = useState(false);
    const busy = isSending || sending;
    const trimmed = body.trim();
    const canSend = canReply && trimmed.length > 0 && !busy;

    async function handleSend() {
        if (!canSend) return;
        setSending(true);
        try {
            await onSend(trimmed, files);
            setBody('');
            setFiles([]);
        } catch {
            return;
        } finally {
            setSending(false);
        }
    }

    return (
        <div className="flex flex-col gap-4">
            {isLoading && <p className="text-sm text-raro-gray-500">Carregando conversa…</p>}

            {!isLoading && messages.length === 0 && (
                <p className="rounded-xl border border-dashed border-raro-gray-200 px-4 py-6 text-center text-sm text-raro-gray-500">
                    Nenhuma mensagem ainda.
                </p>
            )}

            <ul className="flex flex-col gap-3">
                {messages.map(message => {
                    const mine = message.authorEmail.toLowerCase() === viewerEmail.toLowerCase();
                    return (
                        <li key={message.id} className={clsx('flex', mine ? 'justify-end' : 'justify-start')}>
                            <div
                                className={clsx(
                                    'max-w-[85%] rounded-2xl px-4 py-3 text-sm shadow-sm',
                                    mine ? 'rounded-br-md bg-primary-600 text-white' : 'rounded-bl-md border border-raro-gray-200 bg-white text-raro-gray-800'
                                )}
                            >
                                <p className="whitespace-pre-wrap break-words">{message.body}</p>
                                <AttachmentList attachments={message.attachments ?? []} />
                                <p className={clsx('mt-1.5 text-[11px]', mine ? 'text-primary-100' : 'text-raro-gray-400')}>
                                    {message.authorSide === 'dp' ? 'Departamento Pessoal' : mine ? 'Você' : 'Colaborador'} · {formatTime(message.createdAt)}
                                </p>
                            </div>
                        </li>
                    );
                })}
            </ul>

            {canReply ? (
                <div className="space-y-3 rounded-2xl border border-raro-gray-200 bg-white p-4 shadow-sm">
                    <FormTextarea
                        aria-label="Sua mensagem"
                        placeholder={replyPlaceholder}
                        value={body}
                        onChange={event => setBody(event.target.value)}
                        maxLength={MAX_REPLY_LENGTH}
                        showCharCount
                        rows={3}
                    />
                    <AttachmentPicker
                        files={files}
                        onAdd={file => setFiles(current => [...current, file])}
                        onRemove={index => setFiles(current => current.filter((_, position) => position !== index))}
                        disabled={busy}
                        label="Anexos da mensagem"
                    />
                    <div className="flex justify-end">
                        <button
                            type="button"
                            onClick={() => void handleSend()}
                            disabled={!canSend}
                            className="inline-flex items-center gap-2 rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <PaperAirplaneIcon className="h-4 w-4" aria-hidden="true" />
                            {busy ? 'Enviando…' : 'Enviar'}
                        </button>
                    </div>
                </div>
            ) : (
                <p className="text-center text-sm text-raro-gray-500">Este chamado está encerrado. Envie uma nova mensagem para reabri-lo.</p>
            )}
        </div>
    );
}
